import StudentInputSettings from "../../../shared-components/StudentInputSettings";
import React, { useEffect, useState, useMemo } from "react";
import {
  Stack,
  Typography,
  Dialog,
  Switch,
  Button,
  Divider,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import GeneralLoading from "../../../shared-components/Loading";
import { DEFAULT_PREFS, MARKING_MODES } from "../../../utils/constants";
import { isHolisticMarking } from "../../../utils/preferences";
import { useUpdateAssignmentPreferences } from "../hooks/useUpdateAssignmentPreferences";

export const AssignmentPreferencesDialog = ({ assignment, open, setOpen }) => {
  const [serverPrefs, setServerPrefs] = useState(DEFAULT_PREFS);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const { updatePreferences, saving } = useUpdateAssignmentPreferences({
    assignment: assignment,
  });

  const handleChange = (key, value) => {
    setPrefs((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (assignment?.preferences) {
      const parsed = JSON.parse(assignment?.preferences?.data);

      const sanitized = {
        allow_spelling_assistance: parsed.allow_spelling_assistance !== false,
        allow_copy_paste: parsed.allow_copy_paste !== false,
        hide_marks: Boolean(parsed.hide_marks), // Explicit cast
        marking_mode: parsed.marking_mode || MARKING_MODES.ANALYTIC,
        auto_release_marks: Boolean(parsed.auto_release_marks),
        notify_students_by_email_of_feedback: Boolean(
          parsed.notify_students_by_email_of_feedback,
        ),
      };

      setPrefs(sanitized);
      setServerPrefs(sanitized);
    }
  }, [assignment]);

  const isDirty = useMemo(() => {
    return JSON.stringify(prefs) !== JSON.stringify(serverPrefs);
  }, [prefs, serverPrefs]);
  const handleSave = () => {
    updatePreferences({
      prefs,
      onSuccess: (newPrefs) => {
        setServerPrefs(newPrefs);
        handleClose();
      },
    });
  };

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>Assignment Preferences</DialogTitle>
      <DialogContent>
        <StudentInputSettings
          prefs={prefs}
          onChange={handleChange}
          disabled={saving}
        />
        <Stack
          spacing={2}
          divider={<Divider orientation="horizontal" flexItem />}
        >
          {/* Marking Mode */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack spacing={0.5} sx={{ pr: 4 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Holistic Marking
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Switch from Assessment Objective Level marking to Holistic.
              </Typography>
            </Stack>
            <Switch
              slotProps={{
                input: { role: "switch", "aria-label": "Holistic Marking" },
              }}
              checked={isHolisticMarking(prefs)}
              onChange={(e) =>
                handleChange(
                  "marking_mode",
                  e.target.checked
                    ? MARKING_MODES.HOLISTIC
                    : MARKING_MODES.ANALYTIC,
                )
              }
            />
          </Stack>
          {/* Auto release marks*/}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack spacing={0.5} sx={{ pr: 4 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Auto Release Marks
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Allow students to see their marks as soon as Fruitify has marked
                them
              </Typography>
            </Stack>
            <Switch
              checked={prefs.auto_release_marks}
              onChange={(e) =>
                handleChange("auto_release_marks", e.target.checked)
              }
            />
          </Stack>
          {/* Notify students by email*/}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack spacing={0.5} sx={{ pr: 4 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Notify students of feedback
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Send an email to students notifying them their feedback is ready
              </Typography>
            </Stack>
            <Switch
              checked={prefs.notify_students_by_email_of_feedback}
              onChange={(e) =>
                handleChange(
                  "notify_students_by_email_of_feedback",
                  e.target.checked,
                )
              }
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={
              saving ? (
                <GeneralLoading size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSave}
            disabled={!isDirty || saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
